/**
 * What a watch-list row says about its Polymarket market.
 *
 * `watch_groups.status` alone cannot answer the operator's actual question.
 * It is written 'needs-link' at insert, so a freshly added match reads "no
 * market found" before the bot has been asked — and keeps reading it forever
 * while the bot is down. The live phase from BotBridge (MarketSearchState)
 * fills that gap; this maps the pair to what the row shows.
 *
 * Pure and separate from app.tsx so the state machine is testable on its own.
 */
import type { BetCategory, MarketSearchPhase } from "../types.ts";
import { familyOfKind, type CategoryNeeds, type MarketFamily } from "../market-needs.ts";

/** Visual weight, mapped to the row's existing badge/stripe classes. */
export type Tone = "ok" | "searching" | "warn" | "idle" | "partial";

/**
 * Le categorie ACCESE su questa partita che non hanno un mercato dietro.
 *
 * Solo le accese: se l'operatore ha già spento Over/Under, un OU mancante non è
 * una notizia — è una scelta. Segnalarlo lo stesso addestrerebbe a ignorare
 * l'avviso, che è il modo piu' rapido per rendere inutile un avviso.
 *
 * Nessun mercato risolto non conta come "parziale": quello è il caso "nessun
 * mercato", che ha gia' il suo badge e il suo colore.
 */
export function missingCategories(
  resolvedKinds: readonly string[],
  disabled: readonly BetCategory[],
  needs: CategoryNeeds,
): BetCategory[] {
  if (resolvedKinds.length === 0) return [];
  const presenti = new Set<MarketFamily>();
  for (const k of resolvedKinds) {
    const f = familyOfKind(k);
    if (f) presenti.add(f);
  }
  const spente = new Set(disabled);
  const out: BetCategory[] = [];
  for (const [cat, famiglie] of Object.entries(needs) as Array<[BetCategory, MarketFamily[]]>) {
    if (spente.has(cat) || !famiglie?.length) continue;
    if (famiglie.some((f) => !presenti.has(f))) out.push(cat);
  }
  return out;
}

export type RowMarket = {
  tone: Tone;
  /** the pill, uppercased by CSS */
  badge: string;
  /** one line under the title, or null when the badge says enough */
  hint: string | null;
  /** whether to offer the paste-a-link box — pointless while an answer is coming */
  offerManual: boolean;
};

export function rowMarket(
  status: "resolved" | "needs-link",
  phase: MarketSearchPhase | undefined,
  botRunning: boolean,
  /** categorie accese senza mercato — da `missingCategories` */
  missing: readonly BetCategory[] = [],
): RowMarket {
  // A search on an ALREADY-bound group is the restart replay re-applying a
  // saved binding. Worth its own word: until it lands, the panel says "ok" from
  // the DB while the bot itself still has nothing bound for this match.
  // One word in the pill either way — it is the narrowest column in the row, and
  // both cases answer the same glance-level question ("ready to trade? not yet").
  // Which kind of search it is belongs in the hint, where explanation already lives.
  if (phase === "searching") {
    return status === "resolved"
      ? { tone: "searching", badge: "ricerca…", hint: "Il bot sta riapplicando l'associazione salvata.", offerManual: false }
      : { tone: "searching", badge: "ricerca…", hint: "Cerco il mercato su Polymarket.", offerManual: false };
  }
  if (status === "resolved") {
    // Evento trovato, ma non tutto cio' che l'operatore ha acceso ha un mercato
    // dietro. Non e' un errore da correggere — Polymarket per certe partite
    // quota solo l'esito — ma va detto, perche' la spunta accesa promette una
    // scaletta che non puo' partire. Il dettaglio (quali) vive nel tooltip: il
    // badge e' la colonna piu' stretta della riga.
    if (missing.length > 0) return { tone: "partial", badge: "mercato parziale", hint: null, offerManual: false };
    return { tone: "ok", badge: "mercato ok", hint: null, offerManual: false };
  }

  if (phase === "not-found") {
    return { tone: "warn", badge: "nessun mercato", hint: "Nessun mercato Polymarket trovato — incolla il link qui sotto.", offerManual: true };
  }
  if (phase === "timed-out") {
    return { tone: "warn", badge: "nessuna risposta", hint: "Il bot non ha risposto alla ricerca. Rilancia il pre-check o incolla il link.", offerManual: true };
  }
  // Never asked. Two very different reasons, and the operator can act on only one.
  return botRunning
    ? { tone: "idle", badge: "mai cercata", hint: "In attesa del pre-check.", offerManual: true }
    : { tone: "idle", badge: "mai cercata", hint: "Bot spento: nessuna ricerca parte finché non riparte.", offerManual: false };
}
